import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Phone,
  Clock,
  User,
  Bot,
  MessageSquare,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Smile,
  Meh,
  Frown,
  Search,
  Filter,
  Download,
  Eye,
  Loader2,
  RefreshCw,
  ChevronRight,
  Mail,
  MessageCircle,
  Send,
  Play,
  BarChart3,
  Target,
  Shield
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";

const API_URL = process.env.REACT_APP_API_URL;

const SENTIMENT_CONFIG = {
  positive: { icon: Smile, color: "text-emerald-500", bg: "bg-emerald-50", label: "Positive" },
  neutral: { icon: Meh, color: "text-amber-500", bg: "bg-amber-50", label: "Neutral" },
  negative: { icon: Frown, color: "text-red-500", bg: "bg-red-50", label: "Negative" }
};

const OUTCOME_LABELS = {
  appointment_booked: "Appointment Booked",
  lead_qualified: "Lead Qualified",
  information_provided: "Information Provided",
  transferred: "Transferred",
  voicemail: "Voicemail",
  no_answer: "No Answer",
  callback_requested: "Callback Requested",
  issue_resolved: "Issue Resolved",
  escalated: "Escalated",
  sale_completed: "Sale Completed",
  payment_processed: "Payment Processed",
  survey_completed: "Survey Completed",
  dropped: "Dropped",
  other: "Other"
};

export default function AdminCallIntelligence() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  
  const [filters, setFilters] = useState({
    outcome: "all",
    sentiment: "all",
    search: ""
  });

  const businessId = "platform";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [callsRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/calls?business_id=${businessId}&limit=100`),
        fetch(`${API_URL}/api/v2/calls/analytics?business_id=${businessId}`)
      ]);
      
      if (callsRes.ok) {
        const data = await callsRes.json();
        setCalls(data);
      }
      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load call data");
    } finally {
      setLoading(false);
    }
  };

  const viewCallDetails = async (call) => {
    setSelectedCall(call);
    setShowDetailDialog(true);
    
    // Load full transcript and agent timeline if not already present
    try {
      const [callRes, timelineRes] = await Promise.all([
        fetch(`${API_URL}/api/v2/calls/${call.id}`),
        fetch(`${API_URL}/api/v2/handoff/calls/${call.id}/timeline`)
      ]);
      
      let fullCall = call;
      if (callRes.ok) {
        fullCall = await callRes.json();
      }
      
      if (timelineRes.ok) {
        const timelineData = await timelineRes.json();
        fullCall.agent_timeline = timelineData.timeline || [];
      }
      
      setSelectedCall(fullCall);
    } catch (error) {
      console.error("Failed to load call details:", error);
    }
  };

  const generateSummary = async (callId) => {
    setGeneratingSummary(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/calls/${callId}/summary`, {
        method: "POST"
      });
      
      if (!response.ok) throw new Error("Failed to generate summary");
      
      const data = await response.json();
      setSelectedCall(prev => ({ ...prev, ai_summary: data.summary }));
      toast.success("Summary generated");
    } catch (error) {
      toast.error("Failed to generate summary");
    } finally {
      setGeneratingSummary(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const filteredCalls = calls.filter(call => {
    if (filters.outcome !== "all" && call.outcome !== filters.outcome) return false;
    if (filters.sentiment !== "all" && call.sentiment !== filters.sentiment) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        call.caller_number?.toLowerCase().includes(search) ||
        call.caller_name?.toLowerCase().includes(search) ||
        call.ai_summary?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="call-intelligence-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Call Intelligence</h1>
          <p className="text-muted-foreground">
            AI-powered call analysis, summaries, and insights
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Calls</p>
                  <p className="text-2xl font-bold">{stats.stats?.total_calls || 0}</p>
                </div>
                <Phone className="w-8 h-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Talk Time</p>
                  <p className="text-2xl font-bold">
                    {Math.round((stats.stats?.total_duration || 0) / 60)}m
                  </p>
                </div>
                <Clock className="w-8 h-8 text-blue-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Leads Generated</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.stats?.total_leads || 0}</p>
                </div>
                <Target className="w-8 h-8 text-emerald-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Positive</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.stats?.positive_calls || 0}</p>
                </div>
                <Smile className="w-8 h-8 text-emerald-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg QA Score</p>
                  <p className="text-2xl font-bold">
                    {stats.stats?.avg_qa_score ? `${Math.round(stats.stats.avg_qa_score)}%` : "N/A"}
                  </p>
                </div>
                <Shield className="w-8 h-8 text-purple-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by caller, number, or summary..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>
            <Select
              value={filters.outcome}
              onValueChange={(value) => setFilters({ ...filters, outcome: value })}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Outcome" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outcomes</SelectItem>
                {Object.entries(OUTCOME_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.sentiment}
              onValueChange={(value) => setFilters({ ...filters, sentiment: value })}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Sentiment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sentiments</SelectItem>
                <SelectItem value="positive">Positive</SelectItem>
                <SelectItem value="neutral">Neutral</SelectItem>
                <SelectItem value="negative">Negative</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Calls Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Call Archive ({filteredCalls.length})
          </CardTitle>
          <CardDescription>
            Click on a call to view full details, transcript, and AI analysis
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredCalls.length === 0 ? (
            <div className="text-center py-12">
              <Phone className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">No Calls Found</h3>
              <p className="text-muted-foreground">
                {calls.length === 0 
                  ? "No calls have been archived yet"
                  : "No calls match your current filters"
                }
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Caller</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Agents</TableHead>
                  <TableHead>Sentiment</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>QA</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCalls.map((call) => {
                  const sentimentConfig = SENTIMENT_CONFIG[call.sentiment] || SENTIMENT_CONFIG.neutral;
                  const SentimentIcon = sentimentConfig.icon;
                  
                  return (
                    <TableRow 
                      key={call.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => viewCallDetails(call)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium">{call.caller_name || "Unknown"}</p>
                          <p className="text-sm text-muted-foreground">{call.caller_number}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          {formatDuration(call.duration_seconds)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {call.agents_involved?.length > 0 ? (
                          <div className="flex -space-x-2">
                            {call.agents_involved.slice(0, 3).map((agent, i) => (
                              <div
                                key={i}
                                className="w-6 h-6 rounded-full bg-primary/10 border-2 border-background flex items-center justify-center"
                                title={agent.agent_name}
                              >
                                <Bot className="w-3 h-3" />
                              </div>
                            ))}
                            {call.agents_involved.length > 3 && (
                              <div className="w-6 h-6 rounded-full bg-muted border-2 border-background flex items-center justify-center text-xs">
                                +{call.agents_involved.length - 3}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${sentimentConfig.bg}`}>
                          <SentimentIcon className={`w-3 h-3 ${sentimentConfig.color}`} />
                          <span className={`text-xs ${sentimentConfig.color}`}>
                            {sentimentConfig.label}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {OUTCOME_LABELS[call.outcome] || call.outcome || "Unknown"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {call.qa_score !== null && call.qa_score !== undefined ? (
                          <span className={`font-medium ${
                            call.qa_score >= 80 ? "text-emerald-600" :
                            call.qa_score >= 60 ? "text-amber-600" : "text-red-600"
                          }`}>
                            {Math.round(call.qa_score)}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(call.initiated_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Call Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Call Details
            </DialogTitle>
            <DialogDescription>
              {selectedCall?.caller_name || "Unknown"} • {selectedCall?.caller_number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCall && (
            <Tabs defaultValue="summary" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="timeline">Agent Timeline</TabsTrigger>
                <TabsTrigger value="transcript">Transcript</TabsTrigger>
                <TabsTrigger value="qa">QA</TabsTrigger>
                <TabsTrigger value="delivery">Delivery</TabsTrigger>
              </TabsList>
              
              <TabsContent value="summary" className="flex-1 overflow-auto">
                <div className="space-y-4 p-1">
                  {/* Call Info */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Duration</p>
                      <p className="font-semibold">{formatDuration(selectedCall.duration_seconds)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Direction</p>
                      <p className="font-semibold capitalize">{selectedCall.direction}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Outcome</p>
                      <p className="font-semibold">{OUTCOME_LABELS[selectedCall.outcome] || "Unknown"}</p>
                    </div>
                  </div>
                  
                  {/* AI Summary */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        AI Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedCall.ai_summary ? (
                        <p className="text-sm leading-relaxed">{selectedCall.ai_summary}</p>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-muted-foreground mb-3">No summary generated yet</p>
                          <Button 
                            size="sm"
                            onClick={() => generateSummary(selectedCall.id)}
                            disabled={generatingSummary}
                          >
                            {generatingSummary ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <FileText className="w-4 h-4 mr-2" />
                                Generate Summary
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Agents Involved */}
                  {selectedCall.agents_involved?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Bot className="w-4 h-4" />
                          Agents Involved
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {selectedCall.agents_involved.map((agent, i) => (
                            <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Bot className="w-4 h-4" />
                                </div>
                                <div>
                                  <p className="font-medium">{agent.agent_name}</p>
                                  <p className="text-xs text-muted-foreground">{agent.agent_type}</p>
                                </div>
                              </div>
                              {agent.transfer_reason && (
                                <Badge variant="outline" className="text-xs">
                                  {agent.transfer_reason}
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Lead Info */}
                  {selectedCall.lead_id && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Target className="w-4 h-4" />
                          Lead Generated
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm">Lead Score</p>
                            <p className="text-2xl font-bold">{selectedCall.lead_score || 0}</p>
                          </div>
                          <Badge className={
                            selectedCall.lead_classification === "hot" ? "bg-red-500" :
                            selectedCall.lead_classification === "warm" ? "bg-amber-500" : "bg-slate-500"
                          }>
                            {selectedCall.lead_classification?.toUpperCase() || "COLD"}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
              
              {/* Agent Timeline Tab - MAS Handoff Audit Trail */}
              <TabsContent value="timeline" className="flex-1 overflow-auto">
                <div className="space-y-4 p-1">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Bot className="w-4 h-4" />
                        Multi-Agent Handoff Timeline
                      </CardTitle>
                      <CardDescription>
                        Complete audit trail of agent-to-agent handoffs during this call
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {selectedCall.agent_timeline?.length > 0 ? (
                        <div className="relative">
                          {/* Timeline line */}
                          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
                          
                          <div className="space-y-4">
                            {selectedCall.agent_timeline.map((entry, i) => {
                              const isFirst = i === 0;
                              const isLast = i === selectedCall.agent_timeline.length - 1;
                              const isBackOffice = entry.agent_class === "back_office";
                              const isVirtual = entry.agent_id?.startsWith("virtual-");
                              
                              return (
                                <div key={entry.id || i} className="relative pl-10">
                                  {/* Timeline dot */}
                                  <div className={`absolute left-2 top-2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                    isFirst ? "bg-emerald-500 border-emerald-500" :
                                    isLast ? "bg-indigo-500 border-indigo-500" :
                                    "bg-background border-primary"
                                  }`}>
                                    <Bot className={`w-3 h-3 ${isFirst || isLast ? "text-white" : "text-primary"}`} />
                                  </div>
                                  
                                  <div className={`p-3 rounded-lg border ${isBackOffice ? "bg-slate-50 border-slate-200" : "bg-background"}`}>
                                    <div className="flex items-start justify-between mb-2">
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{entry.agent_name}</span>
                                          <Badge variant="outline" className="text-xs">
                                            {entry.agent_type}
                                          </Badge>
                                          {isBackOffice && (
                                            <Badge variant="secondary" className="text-xs">Back-Office</Badge>
                                          )}
                                          {isVirtual && (
                                            <Badge variant="destructive" className="text-xs">Virtual</Badge>
                                          )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                          Agent ID: {entry.agent_id?.substring(0, 20)}...
                                        </p>
                                      </div>
                                      <Badge className={
                                        entry.status === "completed" ? "bg-emerald-500" :
                                        entry.status === "in_progress" ? "bg-blue-500" :
                                        entry.status === "failed" ? "bg-red-500" : "bg-slate-500"
                                      }>
                                        {entry.status}
                                      </Badge>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                      <div>
                                        <p className="text-xs text-muted-foreground">Started</p>
                                        <p>{new Date(entry.started_at).toLocaleTimeString()}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-muted-foreground">Duration</p>
                                        <p>{entry.duration_seconds ? `${entry.duration_seconds.toFixed(1)}s` : "-"}</p>
                                      </div>
                                    </div>
                                    
                                    {entry.handoff_reason && (
                                      <div className="mt-2 pt-2 border-t">
                                        <p className="text-xs text-muted-foreground">Handoff Reason</p>
                                        <p className="text-sm">{entry.handoff_reason}</p>
                                      </div>
                                    )}
                                    
                                    {entry.handoff_trigger && (
                                      <div className="mt-2">
                                        <Badge variant="outline" className="text-xs">
                                          Trigger: {entry.handoff_trigger.replace(/_/g, " ")}
                                        </Badge>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Bot className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                          <p className="text-muted-foreground">No agent timeline data available</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Timeline is recorded for calls processed through the MAS Handoff Engine
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="transcript" className="flex-1 overflow-hidden">
                <ScrollArea className="h-[400px]">
                  {selectedCall.transcript?.length > 0 ? (
                    <div className="space-y-3 p-1">
                      {selectedCall.transcript.map((entry, i) => (
                        <div 
                          key={i}
                          className={`flex gap-3 ${
                            entry.speaker === "caller" ? "" : "flex-row-reverse"
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            entry.speaker === "caller" 
                              ? "bg-blue-100 text-blue-600" 
                              : "bg-primary/10 text-primary"
                          }`}>
                            {entry.speaker === "caller" ? (
                              <User className="w-4 h-4" />
                            ) : (
                              <Bot className="w-4 h-4" />
                            )}
                          </div>
                          <div className={`flex-1 p-3 rounded-lg ${
                            entry.speaker === "caller"
                              ? "bg-blue-50 dark:bg-blue-950/20"
                              : "bg-muted"
                          }`}>
                            <p className="text-sm">{entry.text}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(entry.timestamp)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <p className="text-muted-foreground">No transcript available</p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="qa" className="flex-1 overflow-auto">
                <div className="space-y-4 p-1">
                  {/* QA Score */}
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">QA Score</p>
                          <p className={`text-3xl font-bold ${
                            selectedCall.qa_score >= 80 ? "text-emerald-600" :
                            selectedCall.qa_score >= 60 ? "text-amber-600" : "text-red-600"
                          }`}>
                            {selectedCall.qa_score !== null ? `${Math.round(selectedCall.qa_score)}%` : "Not Scored"}
                          </p>
                        </div>
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                          selectedCall.qa_reviewed ? "bg-emerald-100" : "bg-muted"
                        }`}>
                          {selectedCall.qa_reviewed ? (
                            <CheckCircle className="w-8 h-8 text-emerald-600" />
                          ) : (
                            <Shield className="w-8 h-8 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* QA Issues */}
                  {selectedCall.qa_issues?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">QA Issues</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {selectedCall.qa_issues.map((issue, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 rounded bg-amber-50 text-amber-700">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="text-sm">{issue}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Compliance Flags */}
                  {selectedCall.compliance_flags?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base text-red-600">Compliance Flags</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {selectedCall.compliance_flags.map((flag, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 rounded bg-red-50 text-red-700">
                              <XCircle className="w-4 h-4" />
                              <span className="text-sm">{flag}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="delivery" className="flex-1 overflow-auto">
                <div className="space-y-4 p-1">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        Notification Delivery
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedCall.delivery_records?.length > 0 ? (
                        <div className="space-y-3">
                          {selectedCall.delivery_records.map((record, i) => (
                            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                              <div className="flex items-center gap-3">
                                {record.channel === "email" && <Mail className="w-5 h-5 text-blue-500" />}
                                {record.channel === "sms" && <MessageCircle className="w-5 h-5 text-green-500" />}
                                {record.channel === "whatsapp" && <MessageCircle className="w-5 h-5 text-emerald-500" />}
                                <div>
                                  <p className="font-medium capitalize">{record.channel}</p>
                                  <p className="text-xs text-muted-foreground">{record.recipient}</p>
                                </div>
                              </div>
                              <Badge variant={
                                record.status === "delivered" ? "default" :
                                record.status === "sent" ? "secondary" :
                                record.status === "failed" ? "destructive" : "outline"
                              }>
                                {record.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Send className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <p className="text-muted-foreground">No notifications sent for this call</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
